import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import {
  getISOWeekNumber,
  calculateWeeklyOvertime,
  resolveWeeklyHours,
  getWeekStartDate,
  getWeekEndDate,
  type OvertimeBreakdown
} from '../utils/overtimeUtils';
import { getZoneRank } from '../utils/zoneUtils';
import { formatDateLocal } from '../utils/dateUtils';

import { MONTHS_FR } from '../utils/dateUtils';
export { MONTHS_FR };

// --- INTERFACES ---
export interface PayrollRow {
  empId: string;
  name: string;
  role: string;
  customWeeklyHours: number | null;
  totalHours: number;
  overtime: number;
  overtime25: number;
  overtime50: number;
  sundayHours: number;
  weeklyDetails: (OvertimeBreakdown & { weekNumber: number; year: number })[];
  trajet: number;
  transport: number;
  panier: number;
  zoneDetails: string;
  zones: Record<string, number>;
  status: string;
  gdDaysCount: number;
  gdTotalAmount: number;
  gdTotalTravelHours: number;
}

interface TimeEntryNested {
  work_date: string;
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
  meals_count: number;
  indemnity_transport: number;
  indemnity_trajet: number;
  travel_zone: string | number | null;
  is_grand_deplacement?: boolean;
  gd_amount?: number | null;
  gd_travel_hours?: number | null;
  weekly_sheets: {
    status: string;
    year?: number;
    week_number?: number;
    override_weekly_hours?: number | null;
    employee: {
      id: string;
      full_name: string;
      role: string;
      custom_weekly_hours?: number | null;
    } | null;
  } | null;
}

export type FilterMode = 'week' | 'month' | 'year';

export function usePayrollData() {
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [loading, setLoading] = useState(true);

  // --- ÉTATS DU FILTRE DE DATE ---
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeekNumber(new Date()));

  // --- Calcul des dates de début/fin selon le mode ---
  const { startDate, endDate, extendedStartDate, extendedEndDate, periodLabel, dbPeriodStr } = useMemo(() => {
    let start: Date, end: Date, label: string, dbStr: string;
    let extStart: Date, extEnd: Date;

    if (filterMode === 'week') {
      start = getWeekStartDate(selectedYear, selectedWeek);
      end = getWeekEndDate(start);
      extStart = start;
      extEnd = end;
      label = `Semaine ${selectedWeek} (${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })})`;
      dbStr = `S${selectedWeek}_${selectedYear}`;
    } else if (filterMode === 'month') {
      start = new Date(selectedYear, selectedMonth, 1);
      end = new Date(selectedYear, selectedMonth + 1, 0);
      label = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;
      dbStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

      const firstDayWeek = getISOWeekNumber(start);
      const firstDayYear = start.getFullYear();
      const adjustedFirstYear = (firstDayWeek > 50 && start.getMonth() === 0) ? firstDayYear - 1 : firstDayYear;
      extStart = getWeekStartDate(adjustedFirstYear, firstDayWeek);

      const lastDayWeek = getISOWeekNumber(end);
      const lastDayYear = end.getFullYear();
      const adjustedLastYear = (lastDayWeek === 1 && end.getMonth() === 11) ? lastDayYear + 1 : lastDayYear;
      extEnd = getWeekEndDate(getWeekStartDate(adjustedLastYear, lastDayWeek));
    } else {
      start = new Date(selectedYear, 0, 1);
      end = new Date(selectedYear, 11, 31);
      extStart = start;
      extEnd = end;
      label = `Année ${selectedYear}`;
      dbStr = `${selectedYear}`;
    }

    return {
      startDate: formatDateLocal(start),
      endDate: formatDateLocal(end),
      extendedStartDate: formatDateLocal(extStart),
      extendedEndDate: formatDateLocal(extEnd),
      periodLabel: label,
      dbPeriodStr: dbStr
    };
  }, [filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- Navigation temporelle ---
  const handlePrevious = useCallback(() => {
    if (filterMode === 'week') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedWeek(52);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth > 0) {
        setSelectedMonth(selectedMonth - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedMonth(11);
      }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  }, [filterMode, selectedWeek, selectedMonth, selectedYear]);

  const handleNext = useCallback(() => {
    if (filterMode === 'week') {
      if (selectedWeek < 52) {
        setSelectedWeek(selectedWeek + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedWeek(1);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth < 11) {
        setSelectedMonth(selectedMonth + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedMonth(0);
      }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  }, [filterMode, selectedWeek, selectedMonth, selectedYear]);

  const handleToday = useCallback(() => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    setSelectedWeek(getISOWeekNumber(now));
  }, []);

  // --- CHARGEMENT DES DONNÉES ---
  const fetchPayroll = useCallback(async () => {
    setLoading(true);

    const queryStartDate = filterMode === 'month' ? extendedStartDate : startDate;
    const queryEndDate = filterMode === 'month' ? extendedEndDate : endDate;

    const { data, error } = await supabase
      .from('time_entries')
      .select(`
        work_date,
        hours_normal,
        hours_saturday,
        hours_major,
        meals_count,
        indemnity_transport,
        indemnity_trajet,
        travel_zone,
        is_grand_deplacement,
        gd_amount,
        gd_travel_hours,
        weekly_sheets!inner (
          status, year, week_number, override_weekly_hours,
          employee:employees!employee_id (id, full_name, role, custom_weekly_hours)
        )
      `)
      .gte('work_date', queryStartDate)
      .lte('work_date', queryEndDate)
      .eq('weekly_sheets.status', 'validated');

    if (error) {
      console.error("Erreur récupération paie:", error.message);
      setLoading(false);
      return;
    }

    const agg: Record<string, PayrollRow> = {};
    const entriesByEmpAndDay: Record<string, Record<string, { hours: number; zone: string | number }[]>> = {};
    const processedIndemnities: Record<string, Set<string>> = {};
    const processedGDDates: Record<string, Set<string>> = {};
    const weeklyHoursByEmp: Record<string, Record<string, { normal: number; saturday: number; sunday: number; daysInPeriod: number; totalDays: number; hasGD: boolean }>> = {};
    const payrollOverrideMap: Record<string, Record<string, number | null>> = {};
    const entries = data as unknown as TimeEntryNested[];

    // Premier passage : collecter toutes les heures par semaine (période étendue)
    entries?.forEach((e) => {
      if (e.weekly_sheets?.status !== 'validated') return;
      const emp = e.weekly_sheets?.employee;
      if (!emp) return;

      const id = emp.id;
      if (!weeklyHoursByEmp[id]) {
        weeklyHoursByEmp[id] = {};
      }

      const workDate = new Date(e.work_date);
      const weekNum = getISOWeekNumber(workDate);
      const year = workDate.getFullYear();
      const adjustedYear = (weekNum === 1 && workDate.getMonth() === 11) ? year + 1 : year;
      const weekKey = `${adjustedYear}-W${weekNum}`;

      if (!weeklyHoursByEmp[id][weekKey]) {
        weeklyHoursByEmp[id][weekKey] = { normal: 0, saturday: 0, sunday: 0, daysInPeriod: 0, totalDays: 0, hasGD: false };
      }

      weeklyHoursByEmp[id][weekKey].normal += e.hours_normal || 0;
      weeklyHoursByEmp[id][weekKey].saturday += e.hours_saturday || 0;
      weeklyHoursByEmp[id][weekKey].sunday += e.hours_major || 0;
      weeklyHoursByEmp[id][weekKey].totalDays += 1;
      if (e.is_grand_deplacement) {
        weeklyHoursByEmp[id][weekKey].hasGD = true;
      }

      const sheetOverride = e.weekly_sheets?.override_weekly_hours;
      if (sheetOverride != null) {
        if (!payrollOverrideMap[id]) payrollOverrideMap[id] = {};
        payrollOverrideMap[id][weekKey] = sheetOverride;
      }

      const isInExactPeriod = e.work_date >= startDate && e.work_date <= endDate;
      if (isInExactPeriod) {
        weeklyHoursByEmp[id][weekKey].daysInPeriod += 1;
      }
    });

    // Deuxième passage : agréger les données pour la période exacte uniquement
    entries?.forEach((e) => {
      if (e.weekly_sheets?.status !== 'validated') return;

      const emp = e.weekly_sheets?.employee;
      if (!emp) return;

      const isInExactPeriod = e.work_date >= startDate && e.work_date <= endDate;
      if (!isInExactPeriod) return;

      const id = emp.id;
      const hours = (e.hours_normal || 0) + (e.hours_saturday || 0) + (e.hours_major || 0);

      if (!agg[id]) {
        agg[id] = {
          empId: id,
          name: emp.full_name,
          role: emp.role,
          customWeeklyHours: emp.custom_weekly_hours ?? null,
          totalHours: 0,
          overtime: 0,
          overtime25: 0,
          overtime50: 0,
          sundayHours: 0,
          weeklyDetails: [],
          trajet: 0,
          transport: 0,
          panier: 0,
          zoneDetails: '',
          zones: {},
          status: 'Validé',
          gdDaysCount: 0,
          gdTotalAmount: 0,
          gdTotalTravelHours: 0
        };
        entriesByEmpAndDay[id] = {};
        processedIndemnities[id] = new Set();
        processedGDDates[id] = new Set();
      }

      agg[id].totalHours += hours;

      if (!processedIndemnities[id].has(e.work_date)) {
        if (!e.is_grand_deplacement) {
          agg[id].panier += (e.meals_count || 0);
          agg[id].transport += (e.indemnity_transport || 0);
          agg[id].trajet += (e.indemnity_trajet || 0);
        }
        processedIndemnities[id].add(e.work_date);
      }

      if (e.is_grand_deplacement && !processedGDDates[id].has(e.work_date)) {
        agg[id].gdDaysCount += 1;
        agg[id].gdTotalAmount += e.gd_amount || 0;
        agg[id].gdTotalTravelHours += e.gd_travel_hours || 0;
        processedGDDates[id].add(e.work_date);

        if (!entriesByEmpAndDay[id][e.work_date]) {
          entriesByEmpAndDay[id][e.work_date] = [];
        }
        entriesByEmpAndDay[id][e.work_date].push({ hours: 999, zone: 'GD' });
      }

      const zoneStr = e.travel_zone?.toString().toUpperCase();
      const isLastGDDay = !e.is_grand_deplacement && zoneStr === 'GD';
      if (!e.is_grand_deplacement && !isLastGDDay && e.travel_zone && e.travel_zone !== 0 && e.travel_zone !== '0' && hours > 0) {
        if (!entriesByEmpAndDay[id][e.work_date]) {
          entriesByEmpAndDay[id][e.work_date] = [];
        }
        entriesByEmpAndDay[id][e.work_date].push({ hours, zone: e.travel_zone });
      }
    });

    // Calculer les heures supplémentaires et zones pour chaque employé
    Object.values(agg).forEach(r => {
      const empWeekly = weeklyHoursByEmp[r.empId];
      if (empWeekly) {
        Object.entries(empWeekly).forEach(([weekKey, weekData]) => {
          if (weekData.daysInPeriod === 0) return;

          const [yearStr, weekStr] = weekKey.split('-W');
          const year = parseInt(yearStr);
          const weekNum = parseInt(weekStr);

          const hoursLuSa = weekData.normal + weekData.saturday;
          const isBE35hPayroll = r.role === 'bureau_etude';
          const effectiveHoursPayroll = resolveWeeklyHours(payrollOverrideMap[r.empId]?.[weekKey], r.customWeeklyHours);
          const breakdown = calculateWeeklyOvertime(weekNum, hoursLuSa, weekData.sunday, weekData.hasGD || isBE35hPayroll, effectiveHoursPayroll);

          const ratio = weekData.totalDays > 0 ? weekData.daysInPeriod / weekData.totalDays : 1;

          r.overtime25 += breakdown.overtime25 * ratio;
          r.overtime50 += breakdown.overtime50 * ratio;
          r.sundayHours += breakdown.sundayHours * ratio;

          r.weeklyDetails.push({
            ...breakdown,
            overtime25: breakdown.overtime25 * ratio,
            overtime50: breakdown.overtime50 * ratio,
            sundayHours: breakdown.sundayHours * ratio,
            weekNumber: weekNum,
            year
          });
        });
      }

      r.overtime = r.overtime25 + r.overtime50;

      const empDays = entriesByEmpAndDay[r.empId];
      if (empDays) {
        const zonesCount: Record<string, number> = {};

        Object.values(empDays).forEach(dayEntries => {
          if (dayEntries.length === 0) return;

          dayEntries.sort((a, b) => {
            if (b.hours !== a.hours) return b.hours - a.hours;
            return getZoneRank(b.zone) - getZoneRank(a.zone);
          });

          const winningZone = dayEntries[0].zone.toString();
          zonesCount[winningZone] = (zonesCount[winningZone] || 0) + 1;
        });

        r.zones = zonesCount;

        const parts = Object.entries(zonesCount)
          .sort((a, b) => getZoneRank(a[0]) - getZoneRank(b[0]))
          .map(([zone, count]) => `Z${zone} (${count}j)`);
        r.zoneDetails = parts.join(', ');
      }

      r.totalHours = parseFloat(r.totalHours.toFixed(2));
      r.overtime = parseFloat(r.overtime.toFixed(2));
      r.overtime25 = parseFloat(r.overtime25.toFixed(2));
      r.overtime50 = parseFloat(r.overtime50.toFixed(2));
      r.sundayHours = parseFloat(r.sundayHours.toFixed(2));
      r.gdTotalAmount = parseFloat(r.gdTotalAmount.toFixed(2));
      r.gdTotalTravelHours = parseFloat(r.gdTotalTravelHours.toFixed(2));
    });

    // ── INJECTION SECRÉTARIAT (heures automatiques 32h/sem + vendredis extras) ──
    const { data: secEmployees } = await supabase
      .from('employees')
      .select('id, full_name, role, custom_weekly_hours')
      .eq('role', 'secretariat')
      .eq('is_active', true);

    if (secEmployees && secEmployees.length > 0) {
      const { data: secExtraHours } = await supabase
        .from('secretariat_extra_hours')
        .select('employee_id, work_date, hours')
        .gte('work_date', startDate)
        .lte('work_date', endDate);

      for (const sec of secEmployees) {
        if (agg[sec.id]) continue;

        const weekMap: Record<string, { daysInPeriod: number; totalWeekDays: number; fridayHours: number }> = {};
        const pStart = new Date(startDate);
        const pEnd = new Date(endDate);

        const d = new Date(pStart);
        while (d <= pEnd) {
          const dow = d.getDay();
          if (dow >= 1 && dow <= 5) {
            const wn = getISOWeekNumber(d);
            const yr = d.getFullYear();
            const wk = `${yr}-W${wn}`;
            if (!weekMap[wk]) weekMap[wk] = { daysInPeriod: 0, totalWeekDays: 0, fridayHours: 0 };
            if (dow <= 4) weekMap[wk].daysInPeriod++;
          }
          d.setDate(d.getDate() + 1);
        }

        for (const wk of Object.keys(weekMap)) {
          const [yr, wPart] = wk.split('-W');
          const jan4 = new Date(parseInt(yr), 0, 4);
          const dayOfWeek = jan4.getDay() || 7;
          const monday = new Date(jan4);
          monday.setDate(jan4.getDate() - dayOfWeek + 1 + (parseInt(wPart) - 1) * 7);
          let total = 0;
          for (let i = 0; i < 4; i++) {
            const day = new Date(monday);
            day.setDate(monday.getDate() + i);
            if (day.getDay() >= 1 && day.getDay() <= 4) total++;
          }
          weekMap[wk].totalWeekDays = total;
        }

        const extras = (secExtraHours || []).filter(
          (ex: { employee_id: string }) => ex.employee_id === sec.id
        );
        extras.forEach((ex: { work_date: string; hours: number }) => {
          const exDate = new Date(ex.work_date);
          const wn = getISOWeekNumber(exDate);
          const yr = exDate.getFullYear();
          const wk = `${yr}-W${wn}`;
          if (!weekMap[wk]) weekMap[wk] = { daysInPeriod: 0, totalWeekDays: 4, fridayHours: 0 };
          weekMap[wk].fridayHours += (ex.hours || 0);
        });

        const baseHours = parseFloat((32 * 52 / 12).toFixed(2));
        let fridayTotal = 0;
        let ot25 = 0, ot50 = 0;

        Object.values(weekMap).forEach(wk => {
          const ratio = wk.totalWeekDays > 0 ? wk.daysInPeriod / wk.totalWeekDays : 1;
          fridayTotal += wk.fridayHours;

          if (wk.fridayHours > 0) {
            const totalWeekHours = 32 + wk.fridayHours;
            const breakdown = calculateWeeklyOvertime(0, totalWeekHours, 0, false, 32);
            ot25 += breakdown.overtime25 * ratio;
            ot50 += breakdown.overtime50 * ratio;
          }
        });

        agg[sec.id] = {
          empId: sec.id,
          name: sec.full_name,
          role: sec.role,
          customWeeklyHours: 32,
          totalHours: parseFloat((baseHours + fridayTotal).toFixed(2)),
          overtime: parseFloat((ot25 + ot50).toFixed(2)),
          overtime25: parseFloat(ot25.toFixed(2)),
          overtime50: parseFloat(ot50.toFixed(2)),
          sundayHours: 0,
          weeklyDetails: [],
          trajet: 0,
          transport: 0,
          panier: 0,
          zoneDetails: '',
          zones: {},
          status: 'validated',
          gdDaysCount: 0,
          gdTotalAmount: 0,
          gdTotalTravelHours: 0
        };
      }
    }

    setRows(Object.values(agg));
    setLoading(false);
  }, [startDate, endDate, extendedStartDate, extendedEndDate, filterMode]);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  // --- Calculs pour les statistiques ---
  const totalEmployees = rows.length;
  const totalHoursAll = parseFloat(rows.reduce((acc, r) => acc + r.totalHours, 0).toFixed(2));
  const totalOvertimeAll = parseFloat(rows.reduce((acc, r) => acc + r.overtime, 0).toFixed(2));
  const totalOvertime25All = parseFloat(rows.reduce((acc, r) => acc + r.overtime25, 0).toFixed(2));
  const totalOvertime50All = parseFloat(rows.reduce((acc, r) => acc + r.overtime50, 0).toFixed(2));
  const totalSundayAll = parseFloat(rows.reduce((acc, r) => acc + r.sundayHours, 0).toFixed(2));
  const totalPaniersAll = rows.reduce((acc, r) => acc + r.panier, 0);
  const totalGDDaysAll = rows.reduce((acc, r) => acc + r.gdDaysCount, 0);
  const totalGDAmountAll = parseFloat(rows.reduce((acc, r) => acc + r.gdTotalAmount, 0).toFixed(2));
  const totalGDTravelHoursAll = parseFloat(rows.reduce((acc, r) => acc + r.gdTotalTravelHours, 0).toFixed(2));

  return {
    // State
    rows,
    setRows,
    loading,
    // Filter
    filterMode,
    setFilterMode,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedWeek,
    setSelectedWeek,
    // Computed period
    startDate,
    endDate,
    periodLabel,
    dbPeriodStr,
    // Navigation
    handlePrevious,
    handleNext,
    handleToday,
    // Refresh
    refresh: fetchPayroll,
    // Stats
    totalEmployees,
    totalHoursAll,
    totalOvertimeAll,
    totalOvertime25All,
    totalOvertime50All,
    totalSundayAll,
    totalPaniersAll,
    totalGDDaysAll,
    totalGDAmountAll,
    totalGDTravelHoursAll,
  };
}
